<template>
  <hb-expansion-panel
    key="coverageOptions"
    :left-cols="8"
    :right-cols="4"
    :expand-icon-off="!tableRateInsuranceEnabled"
  >
    <template v-slot:title>
      <span class="pt-1">Coverage Option Types</span>
      <span class="pl-6" @click.stop>
        <hb-checkbox
          v-if="isPropertyLevel"
          v-model="coverageData.useCorporateDefaults"
          @change="handleUseCoverageCorporateDefaults"
          :readonly="!coverageData.is_found"
          @mousedown.prevent
        >
          <template v-slot:label>
              <span class="checkbox-label">Use Corporate Default</span>
          </template>
        </hb-checkbox>
      </span>
    </template>
    <template v-slot:actions>
      <span>
        <hb-switch
          right
          :class="tableRateInsuranceEnabled ? `mx-2` : `ml-2 mr-5`"
          v-model="coverageData.is_enabled"
          @change="handleCoverageToggle($event)"
          :readonly="disableForm"
          :disabled="disableForm"
          @click.native.stop
          @mousedown.prevent
        >
          <template v-slot:label class="toggleText">
            {{ coverageData.is_enabled ? 'Enabled' : 'Disabled' }}
          </template>
        </hb-switch>
      </span>
    </template>
    <template v-slot:content v-if="tableRateInsuranceEnabled">
      <hb-form
        label="Coverage Type"
        description="Select the Coverage Option Type to use for this unit type. "
        full
        required
      >
        <hb-radio-group
          row
          :disabled="disableForm"
          v-model="coverageData.coverageType"
          mandatory
          class="pa-0 mb-n3"
        >
          <hb-radio :value="INSURANCE.Library">
            <template v-slot:label> {{ INSURANCE.TYPES.LIBRARY.LABEL }} </template>
            <template v-slot:tooltipBody> Select this option to use coverages listed in coverage options </template>
          </hb-radio>

          <hb-radio :value="INSURANCE.TableRate">
            <template v-slot:label> Table Rate </template>
            <template v-slot:tooltipBody> Select this option to use tiers-based coverage </template>
          </hb-radio>
        </hb-radio-group>
      </hb-form>

      <hb-form
        v-if="coverageData.coverageType === INSURANCE.TableRate"
        label="Name"
        full
        required
      >
        <HbTextField
          v-model="coverageData.coverageName"
          v-validate="'max:256|required'"
          data-vv-name="name"
          data-vv-as="Name"
          :error="errors.has('name')"
          placeholder="Enter Name of Coverage"
          :disabled="disableForm"
        />
      </hb-form>
      <hb-form
        v-if="coverageData.coverageType === INSURANCE.TableRate"
        label="Description"
        full
      >
        <HbTextField
          v-model="coverageData.coverageDescription"
          v-validate="'max:256'"
          data-vv-name="description"
          data-vv-as="Description"
          :error="errors.has('description')"
          placeholder="Describe Coverage"
          :disabled="disableForm"
        />
      </hb-form>

      <hb-form
        v-if="coverageData.coverageType === INSURANCE.TableRate"
        label="Monthly Premiums"
        full
        required
      >
        <v-row
          no-gutters
          class="pa-0 mb-n3"
          v-for="(tier, i) in coverageData.tableRateTiers"
          :key="i"
        >
          <hb-text-field
            condensed
            box
            medium
            v-model="tier.premium_rate"
            v-validate="'required|decimal:2|min_value:0.01'"
            :data-vv-name="`monthlyPremium_${i}`"
            data-vv-as="Monthly Premiums"
            :error="errors.has(`monthlyPremium_${i}`)"
            placeholder="0.01"
            :disabled="disableForm"
            >
            <template v-slot:prepend-inner> $ </template>
          </hb-text-field>

          <div :class="['hb-inline-sentence-text', disableForm ? 'hb-text-lighter' : '']">/$1,000 of coverage upto</div>

          <hb-text-field
            condensed
            box
            medium
            v-model="tier.coverage_limit"
            v-validate="'required|numeric|min_value:1000'"
            :data-vv-name="`coverageAmount_${i}`"
            data-vv-as="Monthly Premiums"
            :error="errors.has(`coverageAmount_${i}`)"
            placeholder="1000"
            :disabled="disableForm"
            >
            <template v-slot:prepend-inner> $ </template>
          </hb-text-field>
          <hb-icon
            v-if="i >= 1 && !(disableForm)"
            @click="removeTier(tier.coverage_limit)"
            class="hand pt-1"
            >mdi-close-circle</hb-icon
          >
        </v-row>
        <a
          v-if="!(disableForm)"
          class="ma-0 pa-0"
          @click="addTier(coverageData.tableRateTiers.length)"
          >+ Add New Tier</a
        >
      </hb-form>

      <hb-form
        v-if="coverageData.coverageType === INSURANCE.TableRate"
        label="Taxable"
        full
      >
        <hb-radio-group
          row
          class="pa-0 mb-n3"
          v-model="coverageData.isTaxable"
          :disabled="disableForm"
        >
          <HbRadio
            label="Yes"
            :value="1"
          />
          <HbRadio
            label="No"
            :value="0"
          />
        </hb-radio-group>
      </hb-form>

      <hb-form
        v-if="coverageData.coverageType === INSURANCE.TableRate"
        label="Prorate In"
        full
      >
        <hb-radio-group
          row
          v-model="coverageData.isProrateIn"
          class="pa-0 mb-n3"
          :disabled="disableForm"
        >
          <HbRadio
            label="Yes"
            :value="1"
          />
          <HbRadio
            label="No"
            :value="0"
          />
        </hb-radio-group>
      </hb-form>

      <hb-form
        v-if="coverageData.coverageType === INSURANCE.TableRate"
        label="Prorate Out"
        full
      >
        <hb-radio-group
          row
          v-model="coverageData.isProrateOut"
          class="pa-0 mb-n3"
          :disabled="disableForm"
        >
          <HbRadio
            label="Yes"
            :value="1"
          />
          <HbRadio
            label="No"
            :value="0"
          />
        </hb-radio-group>
      </hb-form>

      <hb-form
        v-if="coverageData.coverageType === INSURANCE.TableRate"
        label="Income Account"
        required
      >
        <hb-autocomplete
          name="income_account"
          hide-details
          label="Select an Account"
          :items="accounts"
          :clearable="false"
          v-validate="'required'"
          data-vv-as="income account"
          item-value="id"
          item-text="name"
          v-model="coverageData.incomeAccount"
          :disabled="disableForm"
        >
          <template v-slot:selection="{ item }">
            {{ item.code }} : {{ item.name }}
          </template>
          <template v-slot:item="{ item }">
            {{ item.code }} : {{ item.name }}
          </template>
        </hb-autocomplete>
      </hb-form>
      <space-selector
        :selectedSpaceTypes="coverageData.selectedSpaceTypes"
        :useCorporateDefaults="coverageData.useCorporateDefaults"
        :isPropertyLevel="isPropertyLevel"
        @spaceTypeUpdate="spaceTypesUpdated"
      >
      </space-selector>
      <hb-bottom-action-bar
        v-if="showSaveButton"
        @close="handleCancelCoverage"
      >
        <template v-slot:right-actions>
          <hb-btn @click="saveCoverageOptions">Save</hb-btn>
        </template>
      </hb-bottom-action-bar>

      <hb-modal
        size="medium"
        v-model="confirmUncheckCoverageCorporate"
        title="Update Corporate Default Status"
        @close="resetCorporateDefaultCoverage"
        confirmation
      >
        <template v-slot:content>
          <div class="px-6 py-4">
            Are you sure you want to update the Corporate Default Status?
            This will delete your property level data.
          </div>
        </template>
        <template v-slot:actions>
          <hb-btn
            color="destructive"
            @click="setDefaultCoverage()"
            >Update</hb-btn
          >
        </template>
      </hb-modal>
    </template>
  </hb-expansion-panel>
</template>

<script type="text/babel">
import { mapGetters, mapActions } from "vuex";
import api from "../../../assets/api.js";
import { notificationMixin } from "../../../mixins/notificationMixin.js";
import INSURANCE from "@/constants/insurance.js";
import SpaceSelector from "./SpaceSelector.vue";
import { cloneDeep, isEqual, omit } from "lodash";

export default {
  name: "CoverageOptions",
  mixins: [notificationMixin],
  props: {
    activeTab: {
      type: String,
      required: true,
      default: "property",
    },
    coverage: {
      required: true,
    },
    level: { type: String, required: true },
  },
  data() {
    return {
      accounts:[],
      coverageData: {},
      confirmUncheckCoverageCorporate: false,
    };
  },
  async created() {
    this.coverageData = cloneDeep(this.coverage);
    this.INSURANCE = INSURANCE;
    await this.fetchAccounting();
  },
  components: {
    SpaceSelector,
  },
  computed: {
    ...mapGetters({
      defaultProperty: "propertiesStore/defaultProperty",
      properties: "propertiesStore/filtered",
      hasRolePermission: "authenticationStore/rolePermission",
      unitTypes: "filterStore/unit_type",
      companySettings: 'authenticationStore/getSettings',
    }),
    showSaveButton() {
      const filteredCoverageData = omit(this.coverageData, ['useCorporateDefaults', 'is_enabled']);
      const filteredCoverage = omit(this.coverage, ['useCorporateDefaults', 'is_enabled']);
      
      const isSettingChanged = !isEqual(filteredCoverageData, filteredCoverage);
      if (this.level !== "property") return isSettingChanged;
      return this.coverageData.is_found && !this.coverageData.useCorporateDefaults && isSettingChanged;
    },    
    isPropertyLevel() {
      return this.level === "property";
    },
    tableRateInsuranceEnabled() {
      return this.companySettings?.allowTableRateCoverage === '1';
    },
    disableForm(){
      return this.isPropertyLevel && this.coverageData.useCorporateDefaults;
    },
  },

  watch: {
    coverage: {
      handler(val){
        this.coverageData = cloneDeep(val);
     },
     deep: true
    },
  },

  methods: {
    ...mapActions({
      setDefaultProperty: "propertiesStore/setDefaultProperty",
    }),
    async fetchAccounting() {
      let p = await api.get(this, api.ACCOUNTING + "accounts");
      this.accounts = p.accounts
        ? p.accounts.filter(
            (a) =>
              a.category &&
              ["income", "expense", "liability"].includes(
                a.category.toLowerCase()
              )
          )
        : [];
    },
    spaceTypesUpdated(types){
      if(!types.includes(this.activeTab)){
        const currentUnit = this.unitTypes.find(x=>x.id === this.activeTab).name;
        this.showMessageNotification({ description: `${currentUnit} cannot be removed` });
        types.unshift(this.activeTab);
      }
      this.coverageData.selectedSpaceTypes = types;
    },
    async validateOpenForm() {
      let status = await this.$validator.validateAll();
      if(this.coverageData.coverageType === INSURANCE.TableRate && this.coverageData.tableRateTiers.length < 1){
        status = false;
        this.showMessageNotification({description: 'Table Rate Insurance with at least 1 tier is required for Table Rate Insurance Template' });
      }
      return status;
    },
    handleCancelCoverage() {
      this.$emit("handleCancelCoverage");
    },
    handleCoverageToggle(value) {
      this.coverageData.is_enabled = value || false;
      this.$emit("handleCoverageToggle", {...this.coverageData });
    },
    async saveCoverageOptions() {    
      let status = await this.validateOpenForm();
      if (!status) return;
      this.$emit("saveCoverageOptions", this.coverageData);
    },
    addTier() {
      this.coverageData.tableRateTiers.push({
        premium_rate: null,
        coverage_limit: null,
      });
    },
    removeTier(coverage_limit) {
      this.coverageData.tableRateTiers = this.coverageData.tableRateTiers
        ? this.coverageData.tableRateTiers.filter(
            (tier) => tier.coverage_limit !== coverage_limit
          )
        : [];
    },
    handleUseCoverageCorporateDefaults(value) {
      if (value) this.confirmUncheckCoverageCorporate = true;
      this.$emit("handleUseCoverageCorporateDefaults", value);
    },
    setDefaultCoverage() {
      this.$emit("setDefaultCoverage");
      this.confirmUncheckCoverageCorporate = false;
    },
    resetCorporateDefaultCoverage() {
      this.coverageData.useCorporateDefaults = false;
    },
  },
};
</script>

<style lang="scss">
  .toggleText {
    font-size: 10px;
    font-weight: 300;
  }
  .checkbox-label{
    padding-top: 2px;
  }
</style>
